import { useState, useEffect, useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { orderService } from '../../../data';
import { API_ENDPOINTS } from '../../../utils/endpoints';
import { getApi } from '../../../utils/api';
import Projection from '../Projection';
import React from 'react';

const SettingsContainer = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;

  @media (max-width: 768px) {
    padding: 15px;
    max-width: 100%;
  }
`;

const Title = styled.h2`
  color: #4A3C31;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    font-size: 1.25rem;
    margin-bottom: 15px;
  }
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 8px;
  color: #4A3C31;

  @media (max-width: 768px) {
    font-size: 0.9rem;
    margin-bottom: 6px;
  }
`;

const Button = styled.button`
  background-color: #C5A572;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background-color: #2C1810;
  }

  &:disabled {
    background-color: #ccc;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    padding: 10px 14px;
    font-size: 13px;
    width: 100%;
  }
`;

const ProjectionButton = styled(Button)`
  margin-left: auto;
  background: #A68B5B;
  color: #fff;
  font-weight: 500;
  box-shadow: none;
  border: 1px solid #A68B5B;
  margin-left: 1rem;
  &:hover {
    background: #4A3C31;
    color: #fff;
  }

  @media (max-width: 768px) {
    margin-left: 0;
    margin-bottom: 15px;
  }
`;

const TitleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    margin-bottom: 15px;
  }
`;

const CenteredButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    justify-content: stretch;
    margin-bottom: 15px;
  }
`;

const FormContainer = styled.div`
  background: white;
  padding: 1.5rem;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 1rem;
  height: fit-content;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    position: static;
    padding: 1rem;
    margin-top: 15px;
  }
`;

const FormTitle = styled.h2`
  color: #4A3C31;
  font-size: 1.2rem;
  margin-bottom: 1rem;
  padding-bottom: 0.5rem;
  border-bottom: 2px solid #E8D5B5;

  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 0.75rem;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    margin-bottom: 0.75rem;
  }
`;

const Select = styled.select`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #E8D5B5;
  border-radius: 4px;
  margin-bottom: 1rem;

  @media (max-width: 768px) {
    padding: 0.75rem;
    font-size: 0.9rem;
    margin-bottom: 0.75rem;
  }
`;

const Input = styled.input`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #E8D5B5;
  border-radius: 4px;
  margin-bottom: 0.5rem;
  
  &:disabled {
    background-color: #f5f5f5;
    cursor: not-allowed;
    opacity: 0.6;
  }

  @media (max-width: 768px) {
    padding: 0.75rem;
    font-size: 0.9rem;
    margin-bottom: 0.4rem;
  }
`;

const DayInputsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.5rem;
  margin-bottom: 1rem;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 0.4rem;
    margin-bottom: 0.75rem;
  }
`;

const DateRangeContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    gap: 0.4rem;
  }
`;

const DateInput = styled(Input)`
  margin-bottom: 0; /* Remove default margin to prevent overflow */
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0.75rem;
    font-size: 0.9rem;
    min-height: 44px; /* Better touch target for mobile */
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
    padding: 0.8rem;
  }
`;

const QuantityInput = styled(Input)`
  margin-bottom: 0;
  width: 100%;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: 0.6rem;
    font-size: 0.9rem;
    min-height: 40px;
  }

  @media (max-width: 480px) {
    font-size: 0.85rem;
    padding: 0.7rem;
  }
`;

const DayContainer = styled.div`
  width: 100%;
  box-sizing: border-box;
`;

const MainContentLayout = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 2rem;
  align-items: start;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr 1fr;
    gap: 1.5rem;
  }

  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
`;

const DAYS_OF_WEEK = [
  'Sunday',     // 1
  'Monday',     // 2
  'Tuesday',    // 3
  'Wednesday',  // 4
  'Thursday',   // 5
  'Friday',     // 6
  'Saturday'    // 7
];

// Helper to get days in a month
function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

// Helper to get the day of week for the 1st of the month (0=Sunday)
function getFirstDayOfWeek(year, month) {
  return new Date(year, month, 1).getDay();
}

// Helper function to check if a day of week is disallowed
function isDayDisallowed(dayOfWeek, product) {
  if (!product) return false;
  
  // Handle different possible data structures for disallow field
  if (product.day_disallow && Array.isArray(product.day_disallow)) {
    return product.day_disallow[dayOfWeek] || false;
  } else if (product.day_disallow && typeof product.day_disallow === 'object') {
    // Handle object with string keys like {"1": true, "2": false, ...}
    return product.day_disallow[dayOfWeek.toString()] || false;
  } else if (product.disallow_by_day && typeof product.disallow_by_day === 'object') {
    return product.disallow_by_day[dayOfWeek] || false;
  } else if (product.days && Array.isArray(product.days)) {
    // If product has a days array with individual day records
    const dayRecord = product.days.find(d => d.day_of_week === dayOfWeek);
    return dayRecord?.disallow || false;
  }
  
  return false;
}

// Calendar component for a product's standing orders
const StandingOrderCalendar = ({ orders, productName, market, customerId, reloadOrders, product }) => {
  // All hooks at the top level
  const today = new Date();
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [editingDate, setEditingDate] = useState(null);
  const [editingValue, setEditingValue] = useState('');
  const [saving, setSaving] = useState(false);

  // Add CSS for tooltip hover effect
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      .calendar-cell:hover .lead-time-tooltip {
        opacity: 1 !important;
      }
    `;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // Flatten all days for this product's orders into a map: { 'YYYY-MM-DD': quantity }
  const dayQuantityMap = useMemo(() => {
    const map = {};
    orders.forEach(order => {
      const start = new Date(order.start_date + 'T00:00:00');
      const end = order.end_date ? new Date(order.end_date + 'T00:00:00') : null;
      order.days.forEach(dayObj => {
        let current = new Date(start);
        const targetDay = (dayObj.day - 1) % 7;
        while (current.getDay() !== targetDay) {
          current.setDate(current.getDate() + 1);
          if (end && current > end) return;
        }
        while (!end || current <= end) {
          const key = current.toISOString().split('T')[0];
          map[key] = dayObj.quantity;
          current.setDate(current.getDate() + 7);
          if (end && current > end) break;
        }
      });
    });
    return map;
  }, [orders]);

  // Add click-away logic for editing
  useEffect(() => {
    if (!editingDate) return;
    function handleClick(event) {
      if (event.target.closest('.calendar-edit-input')) return;
      setEditingDate(null);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [editingDate]);

  const daysInMonth = getDaysInMonth(currentYear, currentMonth);
  const firstDayOfWeek = getFirstDayOfWeek(currentYear, currentMonth);

  // Build calendar grid
  const calendarDays = [];
  for (let i = 0; i < firstDayOfWeek; i++) {
    calendarDays.push(null); // Empty cells for days before the 1st
  }
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  // Navigation handlers
  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(y => y - 1);
    } else {
      setCurrentMonth(m => m - 1);
    }
  };
  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(y => y + 1);
    } else {
      setCurrentMonth(m => m + 1);
    }
  };

  // Handle save
  const handleSave = async (dateStr) => {
    setSaving(true);
    const [year, month, day] = dateStr.split('-').map(Number);
    const jsDay = new Date(year, month - 1, day).getDay();
    const dayOfWeek = jsDay === 0 ? 1 : jsDay + 1; // 1=Sunday, 2=Monday, ..., 7=Saturday
    console.log('dateStr:', dateStr, 'jsDay:', jsDay, 'dayOfWeek:', dayOfWeek);
    
    // Check if orders are disallowed for this day of week
    const isDisallowed = isDayDisallowed(dayOfWeek, product);
    
    if (isDisallowed) {
      alert(`Orders are not allowed for ${['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayOfWeek - 1]}. Please select a different day.`);
      setSaving(false);
      return;
    }
    
    // Check minimum quantity for this day of week
    const minQty = product?.day_min_qty?.[dayOfWeek] || 0;
    const quantity = Number(editingValue);
    
    if (quantity < minQty) {
      alert(`Minimum quantity for ${['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayOfWeek - 1]} is ${minQty}. Please enter a quantity of ${minQty} or higher.`);
      setSaving(false);
      return;
    }
    
    const payload = {
      market,
      product_id: product.id,
      overrides: [
        {
          start_date: dateStr,
          end_date: dateStr,
          day_of_week: dayOfWeek,
          quantity: quantity
        }
      ]
    };
    try {
      await orderService.updateStandingOrders(customerId, payload);
      setEditingDate(null);
      setEditingValue('');
      if (reloadOrders) await reloadOrders();
    } catch (err) {
      alert('Failed to update standing order.');
    } finally {
      setSaving(false);
    }
  };

  // Use product.lead_time and product.start_date for minEditableDate
  // Ensure all dates are compared at midnight local time.
  const todayForMinMax = new Date();
  todayForMinMax.setHours(0, 0, 0, 0); // Midnight local time today

  const minDateFromLeadTime = new Date(todayForMinMax);
  minDateFromLeadTime.setDate(todayForMinMax.getDate() + (product?.lead_time || 0));
  // minDateFromLeadTime is now midnight local time, today + lead_time

  let minDateFromProductApi;
  if (product?.start_date) {
    // product.start_date is "YYYY-MM-DDTHH:mm:ss"
    // We only care about the date part for comparison against other midnight dates.
    const datePart = product.start_date.substring(0, 10); // "YYYY-MM-DD"
    const [year, month, day] = datePart.split('-').map(Number);
    minDateFromProductApi = new Date(year, month - 1, day); // Midnight local time
  }

  let calculatedMinEditableDate;
  if (minDateFromProductApi) {
    // Compare timestamps to ensure correct date comparison
    calculatedMinEditableDate = minDateFromLeadTime.getTime() > minDateFromProductApi.getTime() 
                                ? minDateFromLeadTime 
                                : minDateFromProductApi;
  } else {
    // If no product.start_date, min is determined by lead time from today
    calculatedMinEditableDate = minDateFromLeadTime;
  }
  const minEditableDate = calculatedMinEditableDate;

  const maxEditableDate = new Date(todayForMinMax.getFullYear() + 1, 11, 31); // Dec 31st of next year
  maxEditableDate.setHours(0, 0, 0, 0); // Midnight local time

  // Helper to check if a date is editable
  function isEditable(dateStr) { // dateStr is 'YYYY-MM-DD'
    const [year, month, day] = dateStr.split('-').map(Number);
    // Creates a local date at midnight for the calendar cell
    const cellDate = new Date(year, month - 1, day); 
    // All dates (cellDate, minEditableDate, maxEditableDate) are local midnight
    // Compare timestamps for robust date comparison
    
    // Check if date is within allowed range
    const withinDateRange = cellDate.getTime() >= minEditableDate.getTime() && cellDate.getTime() <= maxEditableDate.getTime();
    
    // Check if orders are disallowed for this day of week
    const jsDay = cellDate.getDay();
    const dayOfWeek = jsDay === 0 ? 1 : jsDay + 1; // 1=Sunday, 2=Monday, ..., 7=Saturday
    const isDisallowed = isDayDisallowed(dayOfWeek, product);
    
    return withinDateRange && !isDisallowed;
  }

  return (
    <div style={{ marginBottom: '2rem', background: 'white', borderRadius: 8, padding: 16, boxShadow: '0 1px 3px rgba(0,0,0,0.07)' }}>
      <h2 style={{ color: '#4A3C31', fontSize: '1.2rem', marginBottom: '1rem', borderBottom: '2px solid #E8D5B5', paddingBottom: '0.5rem' }}>{productName}</h2>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <button onClick={prevMonth}>&lt;</button>
        <span style={{ fontWeight: 600 }}>{new Date(currentYear, currentMonth).toLocaleString('default', { month: 'long', year: 'numeric' })}</span>
        <button onClick={nextMonth}>&gt;</button>
      </div>
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between', 
        marginBottom: 8,
        padding: '8px 12px',
        backgroundColor: '#F5EDE3',
        borderRadius: '4px',
        border: '1px solid #E8D5B5'
      }}>
        <span style={{ fontSize: '14px', color: '#4A3C31', fontWeight: '500' }}>
          Lead Time: {product?.lead_time || 0} days
        </span>
        <span style={{ fontSize: '12px', color: '#A68B5B', fontStyle: 'italic' }}>
          Orders must be modified at least {product?.lead_time || 0} days in advance
        </span>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, background: '#F9F6F2', borderRadius: 4, padding: 8 }}>
        {['Sun','Mon','Tue','Wed','Thu','Fri','Sat'].map(d => <div key={d} style={{ fontWeight: 600, color: '#4A3C31', textAlign: 'center' }}>{d}</div>)}
                 {calendarDays.map((day, idx) => {
           if (day === null) return <div key={idx} />;
           const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
           const quantity = dayQuantityMap[dateStr];
           const isEditing = editingDate === dateStr;
           const editable = isEditable(dateStr);
           
           // Calculate day of week for this calendar day
           const jsDay = new Date(currentYear, currentMonth, day).getDay();
           const dayOfWeek = jsDay === 0 ? 1 : jsDay + 1; // 1=Sunday, 2=Monday, ..., 7=Saturday
           
           // Check if this day is disallowed
           const isDisallowed = isDayDisallowed(dayOfWeek, product);
           
           return (
            <div key={idx} style={{
              minHeight: 40,
              textAlign: 'center',
              color: editable ? '#2C1810' : (isDisallowed ? '#ff6b6b' : '#999'),
              fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
              display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
              padding: 2,
              opacity: editable ? 1 : 0.6,
              position: 'relative',
              backgroundColor: isDisallowed ? '#fff5f5' : 'transparent',
              borderRadius: isDisallowed ? '4px' : '0px',
            }} className="calendar-cell">
              <span style={{
                fontWeight: 600,
                fontSize: 16,
                marginBottom: 2,
                letterSpacing: 0.5,
              }}>{day}</span>
              {isEditing ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 4 }} className="calendar-edit-input">
                  <input
                    type="number"
                    value={editingValue}
                    min={product?.day_min_qty?.[dayOfWeek] || 0}
                    disabled={saving}
                    autoFocus
                    style={{
                      fontWeight: 700,
                      fontSize: 15,
                      color: '#A68B5B',
                      letterSpacing: 0.5,
                      background: '#fffbe6',
                      border: '1px solid #C5A572',
                      borderRadius: 4,
                      padding: '2px 4px',
                      minWidth: 16,
                      width: 32,
                      display: 'inline-block',
                      textAlign: 'center',
                    }}
                    onChange={e => setEditingValue(e.target.value)}
                    onKeyDown={e => {
                      if (e.key === 'Escape') setEditingDate(null);
                    }}
                    title={`Minimum quantity: ${product?.day_min_qty?.[dayOfWeek] || 0}`}
                  />
                  <button
                    onClick={() => handleSave(dateStr)}
                    disabled={saving}
                    style={{
                      marginLeft: 4,
                      padding: '2px 8px',
                      borderRadius: 4,
                      border: 'none',
                      background: '#C5A572',
                      color: 'white',
                      fontWeight: 600,
                      cursor: saving ? 'not-allowed' : 'pointer',
                      fontSize: 13,
                    }}
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              ) : (
                <span
                  style={{
                    fontWeight: 700,
                    fontSize: 15,
                    color: editable ? (quantity < (product?.day_min_qty?.[dayOfWeek] || 0) ? '#ff6b6b' : '#A68B5B') : (isDisallowed ? '#ff6b6b' : '#ccc'),
                    letterSpacing: 0.5,
                    background: editable ? (quantity < (product?.day_min_qty?.[dayOfWeek] || 0) ? '#fff5f5' : '#fffbe6') : (isDisallowed ? '#fff5f5' : '#f5f5f5'),
                    border: editable ? (quantity < (product?.day_min_qty?.[dayOfWeek] || 0) ? '1px solid #ff6b6b' : '1px solid #C5A572') : (isDisallowed ? '1px solid #ff6b6b' : '1px solid #ddd'),
                    borderRadius: 4,
                    padding: '2px 8px',
                    minWidth: 24,
                    display: 'inline-block',
                    cursor: editable ? 'pointer' : 'not-allowed',
                    position: 'relative',
                  }}
                  onClick={() => {
                    if (editable) {
                      setEditingDate(dateStr);
                      setEditingValue(quantity !== undefined ? quantity : 0);
                    }
                  }}
                  title={!editable ? (isDisallowed ? `Orders are not allowed for ${['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'][dayOfWeek - 1]}` : `Orders must be placed ${product?.lead_time || 0} days in advance`) : 
                    `Quantity: ${quantity !== undefined ? quantity : 0}${product?.day_min_qty?.[dayOfWeek] ? `, Min: ${product.day_min_qty[dayOfWeek]}` : ''}`}
                >
                  {quantity !== undefined ? quantity : 0}
                  {!editable && (
                    <div style={{
                      position: 'absolute',
                      top: '-20px',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      background: isDisallowed ? '#ff6b6b' : '#4A3C31',
                      color: 'white',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      fontSize: '10px',
                      whiteSpace: 'nowrap',
                      opacity: 0,
                      transition: 'opacity 0.2s',
                      pointerEvents: 'none',
                    }} className="lead-time-tooltip">
                      {isDisallowed ? 'Orders not allowed' : `Lead time: ${product?.lead_time || 0} days`}
                    </div>
                  )}
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SettingsTab = ({ market, customerId }) => {
  // State hooks
  const [standingOrders, setStandingOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedProduct, setSelectedProduct] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [products, setProducts] = useState([]);
  const [quantities, setQuantities] = useState(
    DAYS_OF_WEEK.reduce((acc, _, index) => ({ ...acc, [index + 1]: 0 }), {})
  );
  const [showProjection, setShowProjection] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Function to get quantities for a date range
  const getQuantitiesForDateRange = useCallback((productId, start, end) => {
    const selectedProduct = products.find(p => p.id === Number(productId));
    if (!selectedProduct) {
      console.log('Product not found:', productId);
      return DAYS_OF_WEEK.reduce((acc, _, index) => ({ ...acc, [index + 1]: 0 }), {});
    }

    const overlappingOrders = standingOrders.filter(order => {
      const orderStart = new Date(order.start_date);
      const orderEnd = order.end_date ? new Date(order.end_date) : new Date('9999-12-31');
      const selectedStart = new Date(start);
      const selectedEnd = new Date(end);
      return orderStart <= selectedEnd && orderEnd >= selectedStart;
    });

    return DAYS_OF_WEEK.reduce((acc, _, index) => {
      const dayNumber = index + 1;
      const ordersWithDay = overlappingOrders.filter(order => 
        order.days.some(day => 
          day.day === dayNumber && 
          day.product_name === selectedProduct.description
        )
      );

      if (ordersWithDay.length > 0) {
        const mostRecentOrder = ordersWithDay.sort((a, b) => 
          new Date(b.start_date) - new Date(a.start_date)
        )[0];

        const dayOrder = mostRecentOrder.days.find(d => 
          d.day === dayNumber && 
          d.product_name === selectedProduct.description
        );

        return {
          ...acc,
          [dayNumber]: dayOrder?.quantity || 0
        };
      }

      return {
        ...acc,
        [dayNumber]: 0
      };
    }, {});
  }, [products, standingOrders]);

  // Wrap loadStandingOrders in useCallback
  const loadStandingOrders = useCallback(async () => {
    try {
      setLoading(true);
      const data = await orderService.getStandingOrders(customerId, market);
      setStandingOrders(data);
    } catch (err) {
      console.error('Failed to load standing orders:', err);
      setError('Failed to load standing orders');
    } finally {
      setLoading(false);
    }
  }, [customerId, market]);

  // Function to get minimum valid start date
  const getMinStartDate = useCallback(() => {
    if (!selectedProduct) return '';

    const selectedProductData = products.find(p => p.id === Number(selectedProduct));
    if (!selectedProductData) return '';

    // Get today's date
    const today = new Date();
    // Calculate today + lead time
    const todayPlusLeadTime = new Date(today);
    todayPlusLeadTime.setDate(today.getDate() + (selectedProductData.lead_time || 0));

    // Get product's available start date from API (assume UTC, so just use date part)
    const productStartDate = selectedProductData.start_date
      ? new Date(selectedProductData.start_date)
      : todayPlusLeadTime;

    // Use the later of today+lead_time and product.start_date
    const minDate = todayPlusLeadTime > productStartDate ? todayPlusLeadTime : productStartDate;
    return minDate.toISOString().split('T')[0];
  }, [selectedProduct, products]);

  // Load standing orders when component mounts
  useEffect(() => {
    loadStandingOrders();
  }, [loadStandingOrders]);

  // Update quantities when dates or product changes
  useEffect(() => {
    if (startDate && endDate && selectedProduct) {
      const newQuantities = getQuantitiesForDateRange(selectedProduct, startDate, endDate);
      setQuantities(newQuantities);
    }
  }, [startDate, endDate, selectedProduct, getQuantitiesForDateRange]);

  // Load products when store/market changes only — not when the selected product changes.
  // Including selectedProduct here refetched products after auto-select and could
  // overwrite a successful list with [] when the API blipped.
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const response = await getApi().get(API_ENDPOINTS.ORDERS.PRODUCTS(customerId, market));
        const list = Array.isArray(response.data) ? response.data : [];
        setProducts(list);
      } catch (error) {
        console.error('Error loading products:', error);
        setError('Failed to load products');
      }
    };

    if (customerId && market) {
      loadProducts();
    }
  }, [customerId, market]);

  useEffect(() => {
    if (!selectedProduct && products.length > 0) {
      setSelectedProduct(products[0].id);
    }
  }, [products, selectedProduct]);

  // Function to get all day indices present in the date range
  const getDaysInRange = () => {
    if (!startDate || !endDate) return new Set();
    const start = new Date(startDate + 'T00:00:00');
    const end = new Date(endDate + 'T00:00:00');
    const daysSet = new Set();
    const current = new Date(start);
    while (current <= end) {
      daysSet.add(current.getDay());
      current.setDate(current.getDate() + 1);
    }
    return daysSet;
  };

  const daysInRange = getDaysInRange();
  console.log('daysInRange:', Array.from(daysInRange));

  // Function to check if a day falls within the date range
  const isDayInRange = (dayIndex) => {
    // dayIndex: 0 (Sunday) to 6 (Saturday)
    return daysInRange.has(dayIndex);
  };

  if (loading) {
    return <div>Loading standing orders...</div>;
  }

  if (error) {
    return <div>{error}</div>;
  }

  // Group orders by product
  const ordersByProduct = standingOrders.reduce((acc, order) => {
    const productsInRange = [...new Set(
      order.days
        .filter(day => day.product_name)
        .map(day => day.product_name)
    )];

    productsInRange.forEach(productName => {
      if (!acc[productName]) {
        acc[productName] = {
          product_name: productName,
          orders: []
        };
      }
      
      const orderForProduct = {
        start_date: order.start_date,
        end_date: order.end_date,
        days: order.days.filter(day => day.product_name === productName)
      };
      
      acc[productName].orders.push(orderForProduct);
    });
    
    return acc;
  }, {});

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    // Get the selected product data for minimum quantity validation
    const selectedProductData = products.find(p => p.id === Number(selectedProduct));
    
    // Validate minimum quantities and disallowed days for each day
    const validationErrors = [];
    Object.entries(quantities)
      .filter(([day, _]) => isDayInRange(Number(day) - 1))
      .forEach(([day, quantity]) => {
        const dayOfWeek = parseInt(day);
        const isDisallowed = isDayDisallowed(dayOfWeek, selectedProductData);
        const minQty = selectedProductData?.day_min_qty?.[dayOfWeek] || 0;
        const qty = Number(quantity);
        
        if (isDisallowed && qty > 0) {
          const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
          validationErrors.push(`${dayNames[dayOfWeek - 1]}: orders are not allowed for this day`);
        } else if (!isDisallowed && qty < minQty) {
          const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
          validationErrors.push(`${dayNames[dayOfWeek - 1]}: minimum quantity is ${minQty}, but you entered ${qty}`);
        }
      });
    
    if (validationErrors.length > 0) {
      alert(`Please fix the following quantity errors:\n\n${validationErrors.join('\n')}`);
      setIsSubmitting(false);
      return;
    }
    
    // Build overrides array for each day that is enabled (valid for the range), regardless of quantity
    const overrides = Object.entries(quantities)
      .filter(([day, _]) => isDayInRange(Number(day) - 1))
      .map(([day, quantity]) => ({
        start_date: startDate,
        end_date: endDate || null,
        day_of_week: parseInt(day),
        quantity: Number(quantity)
      }));
    const payload = {
      market,
      product_id: Number(selectedProduct),
      overrides
    };
    try {
      await orderService.updateStandingOrders(customerId, payload);
      await loadStandingOrders();
      setSelectedProduct('');
      setStartDate('');
      setEndDate('');
      setQuantities(DAYS_OF_WEEK.reduce((acc, _, index) => ({ ...acc, [index + 1]: 0 }), {}));
      alert('Standing order updated successfully!');
    } catch (err) {
      alert('Failed to update standing order.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <SettingsContainer>
      {showProjection && (
        <Projection market={market} storeId={customerId} onClose={() => setShowProjection(false)} />
      )}
      <div>
        <TitleRow>
          <Title style={{ marginBottom: 0 }}>Standing Orders</Title>
        </TitleRow>
        <CenteredButtonContainer>
          <ProjectionButton type="button" onClick={() => setShowProjection(true)}>
            Print Ordering Summary
          </ProjectionButton>
        </CenteredButtonContainer>

        {/* Moved Product Selection Dropdown Here */}
        {products.length > 0 && (
          <FormGroup style={{ marginBottom: '1rem', maxWidth: '400px' }}> {/* Added style for spacing and width */}
            <FormLabel>Select Product to View/Edit</FormLabel>
            <Select 
              value={selectedProduct}
              onChange={(e) => setSelectedProduct(e.target.value)}
            >
              <option value="">Select a product</option>
              {products.map(product => (
                <option key={product.id} value={product.id}>
                  {product.description}
                </option>
              ))}
            </Select>
          </FormGroup>
        )}

        {/* Product Selection Dropdown - now integrated into the form, which will be moved up conceptually */}
        {/* The form's product dropdown will control this. We need to ensure the calendar uses selectedProduct */}
        <MainContentLayout>
          <div> {/* Left column for Calendar */}
            {selectedProduct && products.length > 0 && (() => {
              const productObj = products.find(p => p.id === Number(selectedProduct));
              if (!productObj) { // Handle case where selectedProduct might be invalid temporarily or product list is empty
                return <p>Please select a product to view its calendar.</p>;
              }
              
              const productSpecificOrdersData = ordersByProduct[productObj.description];
              const calendarOrders = productSpecificOrdersData ? productSpecificOrdersData.orders : [];

              return (
                <StandingOrderCalendar
                  key={productObj.id} // Use product ID as key
                  orders={calendarOrders}
                  productName={productObj.description}
                  market={market}
                  customerId={customerId}
                  reloadOrders={loadStandingOrders}
                  product={productObj} // Pass the full product object
                />
              );
            })()}
          </div>
          
          <div> {/* Right column for FormContainer */}
            <FormContainer>
              <FormTitle>Add/Modify Standing Order</FormTitle>
              <form onSubmit={handleSubmit}>
                {selectedProduct && (
                  <FormGroup>
                    <FormLabel>Date Range</FormLabel>
                    <DateRangeContainer>
                      <DateInput
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        required
                        min={getMinStartDate()}
                        max={new Date(new Date().getFullYear() + 1, 11, 31).toISOString().split('T')[0]}
                        placeholder="Start Date"
                        style={{
                          backgroundColor: startDate < getMinStartDate() ? '#f5f5f5' : 'white',
                          color: startDate < getMinStartDate() ? '#999' : '#2C1810'
                        }}
                      />
                      <DateInput
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        required
                        min={startDate || getMinStartDate()}
                        max={new Date(new Date().getFullYear() + 1, 11, 31).toISOString().split('T')[0]}
                        placeholder="End Date"
                        style={{
                          backgroundColor: endDate < (startDate || getMinStartDate()) ? '#f5f5f5' : 'white',
                          color: endDate < (startDate || getMinStartDate()) ? '#999' : '#2C1810'
                        }}
                      />
                    </DateRangeContainer>
                  </FormGroup>
                )}

                {selectedProduct && startDate && endDate && (
                  <FormGroup>
                    <FormLabel>Quantities by Day</FormLabel>
                    <DayInputsGrid>
                      {DAYS_OF_WEEK.map((day, index) => {
                        const isInRange = isDayInRange(index);
                        const dayOfWeek = index + 1; // 1=Sunday, 2=Monday, etc.
                        const selectedProductData = products.find(p => p.id === Number(selectedProduct));
                        const isDisallowed = isDayDisallowed(dayOfWeek, selectedProductData);
                        const minQty = selectedProductData?.day_min_qty?.[dayOfWeek] || 0;
                        const currentQty = quantities[dayOfWeek];
                        const isBelowMin = currentQty < minQty;
                        
                        console.log('Rendering day', { day, index, isInRange, value: quantities[index + 1], minQty, isDisallowed });
                        return (
                          <DayContainer key={day}>
                            <FormLabel style={{ 
                              color: isInRange ? (isDisallowed ? '#ff6b6b' : '#4A3C31') : '#999',
                              opacity: isInRange ? 1 : 0.6 
                            }}>
                              {day}
                              {!isDisallowed && (
                                <span style={{ 
                                  fontSize: '11px', 
                                  color: '#A68B5B', 
                                  fontWeight: 'normal',
                                  display: 'block',
                                  marginTop: '2px'
                                }}>
                                  Min: {minQty}
                                </span>
                              )}
                              {isDisallowed && (
                                <span style={{ 
                                  fontSize: '11px', 
                                  color: '#ff6b6b', 
                                  fontWeight: 'normal',
                                  display: 'block',
                                  marginTop: '2px'
                                }}>
                                  Not allowed
                                </span>
                              )}
                            </FormLabel>
                            <QuantityInput
                              type="number"
                              min={isDisallowed ? 0 : minQty}
                              value={quantities[dayOfWeek]}
                              onChange={(e) => setQuantities(prev => ({
                                ...prev,
                                [dayOfWeek]: parseInt(e.target.value) || 0
                              }))}
                              disabled={!isInRange || isDisallowed}
                              style={{
                                backgroundColor: isInRange ? (isDisallowed ? '#fff5f5' : (isBelowMin ? '#fff5f5' : 'white')) : '#f5f5f5',
                                border: isDisallowed ? '1px solid #ff6b6b' : (isBelowMin ? '1px solid #ff6b6b' : '1px solid #ddd'),
                                cursor: isInRange && !isDisallowed ? 'text' : 'not-allowed',
                                opacity: isInRange ? 1 : 0.6
                              }}
                              title={isDisallowed ? `Orders are not allowed for ${day}` : (isBelowMin ? `Minimum quantity for ${day} is ${minQty}` : '')}
                            />
                          </DayContainer>
                        );
                      })}
                    </DayInputsGrid>
                  </FormGroup>
                )}

                <Button 
                  type="submit"
                  disabled={!selectedProduct || !startDate || !endDate || isSubmitting}
                >
                  {isSubmitting ? 'Submitting...' : 'Submit'}
                </Button>
              </form>
            </FormContainer>
          </div>
        </MainContentLayout>
      </div>
    </SettingsContainer>
  );
};

export default SettingsTab;